import { styled } from '@mui/material/styles';
import { Box, Drawer, ListItem, ListItemButton, Button, Select } from '@mui/material';

export const SBWIDTH = 300;

// --- useStyles (default) ---

export const PadBox = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const SystemBlock = styled('div')({
  pageBreakBefore: 'always',
  clear: 'both',
});

export const HeaderRow = styled('div')({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
});

export const HeaderChild = styled('span')({
  // PFM-2615: stack the name and its weight/criteria sub-label vertically.
  // Previously HeaderCriteria was `position: absolute` (pinned to the bottom of
  // this box). With the SystemHeader's 48px line-height the name text is
  // vertically centered, so the absolutely-positioned weight label rendered on
  // top of the name (overlap regression from the MUI v5->v9 upgrade). Laying
  // the children out in a flex column keeps the weight cleanly below the name.
  // Top-align the column so the name (first line) sits at the top next to the
  // checkbox and the weight hangs below it — the header itself top-aligns its
  // row when a weight is present (see SystemHeader).
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  margin: 0,
  overflow: 'hidden',
  position: 'relative',
});

export const HeaderChildName = styled('span')({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  overflowX: 'auto',
  // PFM-2615: the SystemHeader sets line-height:48px (to clamp single-line
  // headers to 48px tall). When a weight/criteria sub-label is stacked below the
  // name, that 48px line on the name pushed the column to ~62px and made the
  // weighted header rows taller than the others (staggered). A normal line-height
  // lets the name + weight fit within the header's 48px min-height, so weighted
  // and unweighted headers stay the same height.
  lineHeight: 1.2,
});

export const HeaderChildScore = styled('span')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  '@media print': {
    display: 'block',
  },
  width: 48,
}));

export const HeaderCriteria = styled('span')(({ theme }) => ({
  // PFM-2615: kept in normal flow (below the name in HeaderChild's column
  // layout) instead of `position: absolute`, which overlapped the name after
  // the MUI v9 upgrade. A real line-height prevents the text from collapsing
  // onto the name baseline.
  lineHeight: 1.2,
  fontSize: '12px',
  color: theme.palette.text.secondary,
  fontWeight: 'normal',
}));

export const SidebarHeaderChild = styled('span')({
  marginRight: 15,
});

export const SystemHeader = styled('div')(({ theme }) => ({
  // In MUI v9, `as={ListSubheader}` no longer reliably carries through
  // ListSubheader's default `position: sticky` style — emotion's styled('div')
  // generates a className whose specificity wins, leaving the header un-sticky.
  // Pin the sticky positioning explicitly so checklist headers don't slide
  // under the system header on scroll.
  position: 'sticky',
  top: 0,
  zIndex: 3,
  paddingLeft: theme.spacing(2),
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  marginTop: '1em',
  marginBottom: '1em',
  // PFM-2599 QA round 2: in v5, `as={ListSubheader}` applied
  // ListSubheader's line-height: 48px, which constrained the header's height
  // so checklist sticky headers stacked underneath cleanly. v9 no longer
  // applies that, and the bare h2 children inherit the browser-default
  // ~0.83em top/bottom margins (plus h2's 1.5em font-size), nearly doubling
  // the header's vertical footprint and pushing the checklist header out of
  // view. Clamp the row's height and reset child margins so the superset
  // header sits flush at 48px tall like prod.
  minHeight: 48,
  lineHeight: '48px',
  '& h2, & h3, & h4': {
    margin: 0,
  },
  // PFM-2615: when the header carries a weight/criteria sub-label, top-align the
  // row so the checkbox/score and the name line up at the top and the weight sits
  // below the name (matching prod). Unweighted headers keep their centered single
  // line. The weight label is tagged with `.weight-criteria` (see SystemScore).
  '&:has(.weight-criteria)': {
    alignItems: 'flex-start',
    '& h2': {
      lineHeight: 1.2,
    },
  },
}));

export const ChecklistMenuHeader = styled('span')({
  display: 'inline',
  fontSize: 'small',
});

export const ContentWrapper = styled('div', {
  shouldForwardProp: (prop) => !['isCourse'].includes(prop),
})(({ isCourse }) => ({
  display: 'flex',
  width: '100%',
  height: 'calc(100% - 176px)',
  '@media print': {
    position: 'relative',
    display: 'block',
    height: 'auto !important',
  },
  ...(isCourse && { height: 'unset' }),
}));

export const ListItemWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  height: '100%',
  alignItems: 'center',
});

export const ListItemWrapChild = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(3),
}));

export const ExpandIcon = styled('span')({
  position: 'absolute',
  right: 5,
});

export const RightContent = styled('div', {
  shouldForwardProp: (prop) => !['isCourse', 'printView'].includes(prop),
})(({ theme, isCourse, printView }) => ({
  position: 'relative',
  width: `calc(100% - ${SBWIDTH}px)`,
  maxWidth: theme.breakpoints.values['lg'],
  [theme.breakpoints.up('xl')]: {
    marginLeft: `calc(50% - ${theme.breakpoints.values['lg'] / 2}px - ${SBWIDTH}px)`,
  },
  right: 0,
  padding: theme.spacing(1),
  [theme.breakpoints.down('lg')]: {
    padding: 0,
    width: '100%',
  },
  '@media print': {
    width: '100%',
  },
  ...(isCourse && {
    width: '100%',
    maxWidth: 'unset',
    [theme.breakpoints.up('xl')]: {
      marginLeft: 'unset',
    },
  }),
  ...(printView && {
    width: '100%',
    [theme.breakpoints.up('xl')]: {
      marginLeft: 'unset',
    },
  }),
}));

export const ActiveEditContainer = styled('div')({});

export const ToggleSidebar = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
  '@media print': {
    display: 'none',
  },
}));

export const ShowImages = styled('div')(({ theme }) => ({
  color: theme.palette.secondary.main,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  '@media print': {
    display: 'none',
  },
}));

export const ChecklistActiveButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  height: '100%',
  alignItems: 'center',
  paddingLeft: theme.spacing(3),
  ...(isActive
    ? {
        fontWeight: 'bold',
        color: theme.palette.text.primary,
        borderTop: `1px solid ${theme.palette.action.disabledBackground}`,
        borderBottom: `1px solid ${theme.palette.action.disabledBackground}`,
      }
    : {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.text.disabled,
      }),
}));

export const SystemActiveButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})(({ theme, isActive }) => ({
  ...(isActive
    ? {
        fontWeight: 'bold',
        color: theme.palette.text.primary,
      }
    : {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.text.disabled,
      }),
}));

export const Grow = styled('div')({
  flexGrow: 1,
});

export const ShowBlock = styled('div')({
  display: 'block',
});

export const HideBlock = styled('div')({
  display: 'none',
});

export const LeftSidebar = styled('div')(({ theme }) => ({
  width: SBWIDTH,
  position: 'sticky',
  alignSelf: 'flex-start',
  top: 0,
  overflowY: 'auto',
  height: '100%',
  maxHeight: '100vh',
}));

export const LeftSidebarMobile = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: '80%',
  },
});

export const DrawerPaper = styled('div')({
  width: SBWIDTH,
  top: 177,
  bottom: 0,
  height: 'auto',

  '& .MuiList-padding': {
    padding: 0,
  },
});

export const OverallScoreItem = styled(ListItem)({
  textAlign: 'center',
  fontWeight: 'bold',
  justifyContent: 'center',
});

export const TaskBadge = styled('span')({
  position: 'absolute',
  top: 4,
  right: 4,
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: 'red',
  zIndex: 1,
});

// --- filterStyles ---

// PFM-2639: className handed to the SearchBar's `containerStyle` so the rule
// below can target its (global) Container without touching SearchBar usages
// elsewhere. Selector lives on FilterContainer so it out-specifies the
// component's own emotion class — no !important needed (same pattern as
// admin/content/styles.js ADMIN_SEARCH_BAR).
export const SCORE_SEARCH_BAR = 'assessment-score-search-bar';

export const FilterContainer = styled('div')({
  top: 0,
  zIndex: 3,
  pointerEvents: 'auto',
  overflow: 'hidden',

  // PFM-2639: keep the search bar fully expanded at every breakpoint. In the
  // mobile filter row the search Container would otherwise flex-shrink down to
  // the magnifier icon, collapsing the input to ~0px so there was nowhere to
  // type. Pin a usable min-width and stop it shrinking; the bordered
  // FilterWrapper already scrolls horizontally to absorb the overflow.
  [`& .${SCORE_SEARCH_BAR}`]: {
    flexShrink: 0,
    minWidth: 200,
  },

  '@media print': {
    display: 'none',
  },
});

export const FilterWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'withBorder',
})(({ theme, withBorder }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  ...(withBorder && {
    overflowX: 'scroll',
    borderBottom: `1px solid ${theme.palette.divider}`,
  }),
}));

export const SearchContainer = styled('div')({
  marginBottom: 'unset',
  width: 240,
  minWidth: 200,
});

export const SwitchContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(1),
}));

export const SwitchFlex = styled('div')({
  display: 'flex',
});

export const ButtonMargin = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(2.5),
  marginRight: theme.spacing(1.5),
  color: 'grey',
}));

export const CenterDiv = styled('div')({
  alignItems: 'center',
  justifyContent: 'center',
});

export const FilterSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  borderRadius: 4,
  marginRight: theme.spacing(1),
  '& .MuiFilledInput-input, & .MuiSelect-select': {
    padding: '5px 12px 6px',
    paddingRight: 32,
  },
  ...(isSelected && {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.common.white} !important`,
  }),
}));

export const ButtonIcon = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

export const SwitchText = styled('span')({
  textWrap: 'nowrap',
});

export const ButtonDropContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  marginLeft: theme.spacing(1.5),
  marginRight: theme.spacing(1.5),
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.disabledBackground : theme.palette.grey[100],
  borderRadius: 4,

  '& .MuiButton-icon, .innericon': {
    color: theme.palette.text.primary,
  },

  // styles previously on classes.buttonDrop applied to the inner Button
  '& > .MuiButtonBase-root': {
    minWidth: 'auto',
    padding: theme.spacing(1),
    color: theme.palette.text.primary,

    '& .MuiButton-icon': {
      color: theme.palette.text.primary,
    },

    '& .innericon': {
      width: 2,
      overflow: 'visible',
      position: 'relative',
      left: -12,
      fontSize: 20,
      color: theme.palette.text.primary,
    },
  },
}));

export const Droptext = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontStyle: 'italic',
  margin: theme.spacing(1),

  '& .difflink': {
    color: theme.palette.secondary.main,
    cursor: 'pointer',
  },
}));

export const ButtonDrop = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(1),
  color: theme.palette.text.primary,

  '& .MuiButton-icon': {
    color: theme.palette.text.primary,
  },

  '& .innericon': {
    width: 2,
    overflow: 'visible',
    position: 'relative',
    left: -12,
    fontSize: 20,
    color: theme.palette.text.primary,
  },
}));

// --- multiStyles ---

export const ScorecardTitle = styled('div')(({ theme }) => ({
  fontWeight: 'bold',
  textAlign: 'center',
  marginBottom: theme.spacing(1),
}));
